import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Metered contact reveals.
 *
 * A member's subscription buys access to contact details. A buyer reveals a
 * seller's number on a listing; a seller reveals a buyer's number on a wanted
 * ad. Same allowance either way.
 *
 * Numbers are never sent to the browser with the listing — they only come back
 * from reveal_contact(), which checks the allowance and records the reveal. That
 * is what makes the allowance mean anything: if the numbers were readable
 * directly, nobody would ever need to spend one.
 */

export interface RevealAllowance {
  signedIn: boolean;
  tier: string | null;
  /** null means unlimited. */
  limit: number | null;
  used: number;
  /** null means unlimited. */
  remaining: number | null;
}

export interface RevealResult {
  revealed: boolean;
  /** Present when revealed is false. 'limit' = allowance used up. */
  reason?: 'limit';
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  /** True when this one had already been paid for, so it cost nothing. */
  alreadyRevealed?: boolean;
  tier?: string;
  limit?: number;
  used?: number;
}

export async function fetchRevealAllowance(
  supabase: SupabaseClient
): Promise<RevealAllowance> {
  const { data, error } = await supabase.rpc('reveal_allowance');
  if (error || !data) {
    return { signedIn: false, tier: null, limit: 0, used: 0, remaining: 0 };
  }
  return data as RevealAllowance;
}

export async function revealListingContact(
  supabase: SupabaseClient,
  listingId: string
): Promise<RevealResult> {
  const { data, error } = await supabase.rpc('reveal_contact', {
    listing: listingId,
    buyer_request: null,
  });
  if (error) throw error;
  return data as RevealResult;
}

export async function revealBuyerRequestContact(
  supabase: SupabaseClient,
  buyerRequestId: string
): Promise<RevealResult> {
  const { data, error } = await supabase.rpc('reveal_contact', {
    listing: null,
    buyer_request: buyerRequestId,
  });
  if (error) throw error;
  return data as RevealResult;
}

/** "3 of 4 left this year", or "Unlimited". */
export function allowanceLabel(a: RevealAllowance): string {
  if (!a.signedIn) return 'Sign in to see contact details';
  if (a.limit === null) return 'Unlimited contact reveals';
  const remaining = a.remaining ?? 0;
  return `${remaining} of ${a.limit} contact reveal${a.limit === 1 ? '' : 's'} left this year`;
}
